import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { GrowlSlice, type GrowUpdate } from '@/store/growl/slice.ts';
import { createAppAsyncThunk } from '@/store/storeMethods.ts';

export const updateGrowlApiErrorThunk = createAppAsyncThunk<void, { error: unknown; growlId: string }>(
  'growl/updateWithHttpError',
  async ({ error, growlId }, { dispatch }) => {
    const statusCode = getStatusCode(error);
    const growlUpdate = getGrowlUpdate(statusCode);

    dispatch(
      GrowlSlice.actions.update({
        growlId,
        data: growlUpdate,
        options: { closeable: true },
      }),
    );
  },
);

function getGrowlUpdate(statusCode: number | undefined): GrowUpdate {
  if (statusCode === undefined) {
    return { status: 'rejected', reason: 'Deal rejected (api error)', errorCode: 'API_ERROR' };
  }
  switch (statusCode) {
    case 408:
      return { status: 'timeout' };
    default:
      return { status: 'rejected', reason: `Deal rejected (api error ${statusCode})`, errorCode: 'API_ERROR' };
  }
}

function getStatusCode(error: unknown): number | undefined {
  if (isFetchBaseQueryErrorWrapper(error)) {
    if (typeof error.error.status === 'number') {
      return error.error.status;
    }
  }
  return undefined;
}

function isFetchBaseQueryErrorWrapper(err: unknown): err is { error: FetchBaseQueryError } {
  if (typeof err === 'object' && err != null && 'error' in err) {
    return isFetchBaseQueryError(err.error);
  }
  return false;
}

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === 'object' && error != null && 'status' in error;
}
